/**
 * @lodex-pro/web-admin — display helpers for Billing > Usage and Invoices (Task 21.7c). Pure.
 */
import { formatCentsAsDollars } from '@lodex-pro/shared/money';
import type { InvoiceStatus, UsageMeter } from './billing-hooks';
import { groupDigits } from '@lodex-pro/shared/number-format';

/** Integer cents → "$1,234.50" (exact: the digits come from formatCentsAsDollars). */
export function money(cents: number): string {
  const plain = formatCentsAsDollars(cents);
  const negative = plain.startsWith('-');
  const [whole = '0', frac = '00'] = (negative ? plain.slice(1) : plain).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}$${grouped}.${frac}`;
}

/** The month before `today` (YYYY-MM-DD) as YYYY-MM: the one a run bills. */
export function previousMonth(today: string): string {
  const [y = 1970, m = 1] = today.split('-').map(Number);
  const pm = m === 1 ? 12 : m - 1;
  const py = m === 1 ? y - 1 : y;
  return `${String(py)}-${String(pm).padStart(2, '0')}`;
}

export const INVOICE_STATUS_LABELS: Readonly<Record<InvoiceStatus, string>> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  PAID: 'Paid',
  VOID: 'Void',
};

export const INVOICE_STATUS_TONES: Readonly<
  Record<InvoiceStatus, 'warning' | 'accent' | 'positive' | 'muted'>
> = {
  DRAFT: 'warning',
  SENT: 'accent',
  PAID: 'positive',
  VOID: 'muted',
};

/** Why a customer was not invoiced (shared PLATFORM_INVOICE_SKIP_REASONS), in words. */
export const SKIP_REASON_LABELS: Readonly<Record<string, string>> = {
  NOT_ACTIVE: 'Billing is not switched on',
  ALREADY_BILLED: 'Already invoiced for this month',
  NO_SUBSCRIPTION: 'No billing plan for this month',
  NO_PUBLISHED_VERSION: 'The rate card has no published version for this month',
  FREE: 'The plan is free',
  USAGE_UNKNOWN: 'Usage was not captured, so nothing is billed as zero',
  NO_MATCHING_BAND: 'A usage figure fits no band on the rate card',
  ZERO_TOTAL: 'The total is $0',
  ERROR: 'Failed while invoicing; create it by hand',
};

export const skipReasonText = (reason: string): string => SKIP_REASON_LABELS[reason] ?? reason;

/** One usage meter as a table cell: its value, "not collected", or "no count taken". */
export function meterCell(m: UsageMeter): string {
  if (!m.collected) return 'not collected';
  if (m.value === null) return 'no count taken';
  if (m.daysCaptured !== null && m.daysCaptured < m.daysInPeriod) {
    return `${groupDigits(m.value)} (${String(m.daysCaptured)} of ${String(m.daysInPeriod)} days)`;
  }
  return groupDigits(m.value);
}

/** The hover help for a cell with no number or a partial count (OPEN-197), else null. */
export function meterHint(m: UsageMeter): string | null {
  if (!m.collected) return "We don't track this yet.";
  if (m.value === null) {
    return 'No daily count was taken this month. Counts are taken each night and cannot be taken for past days.';
  }
  if (m.daysCaptured !== null && m.daysCaptured < m.daysInPeriod) {
    return `Counted on ${String(m.daysCaptured)} of ${String(m.daysInPeriod)} days; the bill uses the highest count on those days.`;
  }
  return null;
}
